import { router } from 'expo-router';
import { Platform, StyleSheet, View } from 'react-native';

import { routes } from '@fhs-legacy/frontend/src/utils/routing';

import { tokens } from '../../tokens';
import { Button } from '../button';
import { IconRedeem } from '../icon';
import { Text } from '../text';

const BANNER_TITLE = 'Have a Gift Card?';
const BANNER_SUBTITLE = 'Add it to the app';
const BUTTON_VISIBLE_TEXT = 'Add Now';

export type AddGiftCardBannerProps = {
  addButtonAccessibilityLabel?: string;
  addButtonAccessibilityHint?: string;
};

/**
 * Same idea as `checkoutFocusableLabeledProps` in cart (FHSD-1256): one labeled tab stop on web plus
 * `accessible` + `accessibilityLabel` so assistive tech does not skip static text and jump only to the button.
 *
 * Avoid XStack/YStack: they default `accessibilityRole` to `none` → web `presentation`, which can remove
 * the row from the accessibility tree.
 */
function giftCardPromoFocusableLabeledProps(accessibilityLabel: string) {
  return {
    accessible: true as const,
    accessibilityLabel,
    accessibilityRole: 'text' as const,
    ...(Platform.OS === 'web' ? { tabIndex: 0 as const } : {}),
  };
}

export function AddGiftCardBanner({
  addButtonAccessibilityLabel = BUTTON_VISIBLE_TEXT,
  addButtonAccessibilityHint = 'Open screen to add a gift card',
}: AddGiftCardBannerProps) {
  const promoA11y = `${BANNER_TITLE} ${BANNER_SUBTITLE}`;

  const iconDecorative = {
    accessibilityElementsHidden: true,
    importantForAccessibility: 'no-hide-descendants' as const,
    ...(Platform.OS === 'web' ? { 'aria-hidden': true as const } : {}),
  };

  const hidePromoTextFromAt = {
    accessibilityElementsHidden: true,
    importantForAccessibility: 'no-hide-descendants' as const,
    ...(Platform.OS === 'web' ? { 'aria-hidden': true as const } : {}),
  };

  const promoWebLabel =
    Platform.OS === 'web'
      ? ({
          'aria-label': promoA11y,
        } as const)
      : {};

  return (
    <View style={styles.row} accessible={false} collapsable={false}>
      <View style={styles.iconContainer} {...iconDecorative}>
        <IconRedeem size={24} />
      </View>

      <View
        style={styles.content}
        collapsable={false}
        {...giftCardPromoFocusableLabeledProps(promoA11y)}
        {...promoWebLabel}
      >
        <Text weight="semibold" {...hidePromoTextFromAt}>
          {BANNER_TITLE}
        </Text>
        <Text style={styles.subtitle} {...hidePromoTextFromAt}>
          {BANNER_SUBTITLE}
        </Text>
      </View>

      <Button
        onPress={() => router.navigate(routes.giftCards)}
        accessibilityRole="button"
        accessibilityLabel={addButtonAccessibilityLabel}
        accessibilityHint={addButtonAccessibilityHint}
      >
        <Button.Text>{BUTTON_VISIBLE_TEXT}</Button.Text>
      </Button>
    </View>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: tokens.colors.$blackOpacity10,
    gap: 12,
  },
  iconContainer: {
    backgroundColor: tokens.colors.$blackOpacity04,
    height: 40,
    width: 40,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 40,
  },
  content: {
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  subtitle: {
    fontSize: 12,
    lineHeight: 19.6,
  },
});
